import apiClient from './api';
import {
  AgentTask,
  TaskReferenceTemplate,
  CustomerTaskProgress,
  TaskDocumentRevision,
  ApiResponse,
} from '@/types';

class TaskPipelineService {
  // ============= Task Definitions =============

  async getTasksForAgent(agentId: string): Promise<AgentTask[]> {
    const response = await apiClient.get<ApiResponse<AgentTask[]>>(`/agents/${agentId}/tasks`);
    return response.data.data || [];
  }

  async createTask(agentId: string, data: Partial<AgentTask>): Promise<AgentTask> {
    const response = await apiClient.post<ApiResponse<AgentTask>>(`/agents/${agentId}/tasks`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create task');
  }

  async updateTask(agentId: string, taskId: string, data: Partial<AgentTask>): Promise<AgentTask> {
    const response = await apiClient.patch<ApiResponse<AgentTask>>(`/agents/${agentId}/tasks/${taskId}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update task');
  }

  async deleteTask(agentId: string, taskId: string): Promise<void> {
    await apiClient.delete(`/agents/${agentId}/tasks/${taskId}`);
  }

  async reorderTasks(agentId: string, taskIds: string[]): Promise<void> {
    await apiClient.post(`/agents/${agentId}/tasks/reorder`, { task_ids: taskIds });
  }

  // ============= Reference Templates =============

  async getReferenceTemplates(): Promise<TaskReferenceTemplate[]> {
    const response = await apiClient.get<ApiResponse<TaskReferenceTemplate[]>>('/reference-templates');
    return response.data.data || [];
  }

  async createReferenceTemplate(data: {
    name: string;
    description?: string;
    content: string;
    original_filename?: string;
    file_type?: string;
  }): Promise<TaskReferenceTemplate> {
    const response = await apiClient.post<ApiResponse<TaskReferenceTemplate>>('/reference-templates', data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create reference template');
  }

  async uploadReferenceTemplate(file: File, name: string, description?: string): Promise<TaskReferenceTemplate> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    if (description) formData.append('description', description);

    const response = await apiClient.post<ApiResponse<TaskReferenceTemplate>>('/reference-templates/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.data) return response.data.data;
    throw new Error('Failed to upload reference template');
  }

  async deleteReferenceTemplate(id: string): Promise<void> {
    await apiClient.delete(`/reference-templates/${id}`);
  }

  // ============= Generated Documents (org-wide) =============

  async getGeneratedDocuments(filters?: {
    customer_id?: string;
    agent_id?: string;
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ documents: CustomerTaskProgress[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.customer_id) params.set('customer_id', filters.customer_id);
    if (filters?.agent_id) params.set('agent_id', filters.agent_id);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));

    const queryString = params.toString();
    const url = `/task-progress/documents${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<ApiResponse<{ documents: CustomerTaskProgress[]; total: number }>>(url);
    return response.data.data || { documents: [], total: 0 };
  }

  // ============= Customer Progress =============

  async getProgressForConversation(conversationId: string): Promise<CustomerTaskProgress[]> {
    const response = await apiClient.get<ApiResponse<CustomerTaskProgress[]>>(`/task-progress/conversation/${conversationId}`);
    return response.data.data || [];
  }

  async getProgressForCustomer(customerId: string): Promise<CustomerTaskProgress[]> {
    const response = await apiClient.get<ApiResponse<CustomerTaskProgress[]>>(`/task-progress/customer/${customerId}`);
    return response.data.data || [];
  }

  async markTaskComplete(progressId: string): Promise<CustomerTaskProgress> {
    const response = await apiClient.post<ApiResponse<CustomerTaskProgress>>(`/task-progress/${progressId}/complete`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to mark task complete');
  }

  async markTaskSkipped(progressId: string): Promise<CustomerTaskProgress> {
    const response = await apiClient.post<ApiResponse<CustomerTaskProgress>>(`/task-progress/${progressId}/skip`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to skip task');
  }

  async approveDocument(progressId: string): Promise<CustomerTaskProgress> {
    const response = await apiClient.post<ApiResponse<CustomerTaskProgress>>(`/task-progress/${progressId}/approve`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to approve document');
  }

  async editDocument(progressId: string, content: string): Promise<CustomerTaskProgress> {
    const response = await apiClient.post<ApiResponse<CustomerTaskProgress>>(`/task-progress/${progressId}/edit`, { content });
    if (response.data.data) return response.data.data;
    throw new Error('Failed to edit document');
  }

  async requestRevision(progressId: string, feedback: string, mode: 'ai_regenerate' | 'reach_customer'): Promise<CustomerTaskProgress> {
    const response = await apiClient.post<ApiResponse<CustomerTaskProgress>>(`/task-progress/${progressId}/revision`, { feedback, mode });
    if (response.data.data) return response.data.data;
    throw new Error('Failed to request revision');
  }

  async regenerateDocument(progressId: string): Promise<CustomerTaskProgress> {
    const response = await apiClient.post<ApiResponse<CustomerTaskProgress>>(`/task-progress/${progressId}/regenerate`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to regenerate document');
  }

  async getRevisionHistory(progressId: string): Promise<TaskDocumentRevision[]> {
    const response = await apiClient.get<ApiResponse<TaskDocumentRevision[]>>(`/task-progress/${progressId}/revisions`);
    return response.data.data || [];
  }

  async downloadDocument(progressId: string, format: 'docx' | 'pdf'): Promise<Blob> {
    const baseUrl = apiClient.defaults.baseURL || '';
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${baseUrl}/task-progress/${progressId}/download/${format}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
    return response.blob();
  }

  // ============= Conversation Attachments =============

  async getAttachmentsForConversation(conversationId: string): Promise<ConversationAttachmentSummary[]> {
    const response = await apiClient.get<ApiResponse<ConversationAttachmentSummary[]>>(`/attachments/conversation/${conversationId}`);
    return response.data.data || [];
  }

  async getAttachmentsForCustomer(customerId: string): Promise<ConversationAttachmentSummary[]> {
    const response = await apiClient.get<ApiResponse<ConversationAttachmentSummary[]>>(`/attachments/customer/${customerId}`);
    return response.data.data || [];
  }

  async uploadAttachment(conversationId: string, file: File, fieldName?: string, agentTaskId?: string): Promise<ConversationAttachmentSummary> {
    const formData = new FormData();
    formData.append('file', file);
    if (fieldName) formData.append('field_name', fieldName);
    if (agentTaskId) formData.append('agent_task_id', agentTaskId);

    const response = await apiClient.post<ApiResponse<ConversationAttachmentSummary>>(`/attachments/conversation/${conversationId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.data) return response.data.data;
    throw new Error('Failed to upload attachment');
  }

  async getAttachment(id: string): Promise<ConversationAttachment> {
    const response = await apiClient.get<ApiResponse<ConversationAttachment>>(`/attachments/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to get attachment');
  }

  async deleteAttachment(id: string): Promise<void> {
    await apiClient.delete(`/attachments/${id}`);
  }
}

export interface ConversationAttachmentSummary {
  id: string;
  conversation_id: string;
  message_id?: string;
  customer_id: string;
  organization_id: string;
  filename: string;
  mime_type: string;
  file_size: number;
  thumbnail_base64?: string;
  field_name?: string;
  agent_task_id?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ConversationAttachment extends ConversationAttachmentSummary {
  content_base64: string;
}

export const taskPipelineService = new TaskPipelineService();
export default taskPipelineService;
